import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CheckCircle2, CloudSun, Loader2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sourceAccent = {
  blend: "border-[#00f2ea]/35 bg-[#00f2ea]/10 text-[#00f2ea]",
  openweather: "border-sky-300/35 bg-sky-300/10 text-sky-200",
  nws: "border-emerald-300/35 bg-emerald-300/10 text-emerald-200",
  open_meteo: "border-amber-300/35 bg-amber-300/10 text-amber-200",
};

const formatValue = (value, suffix = "") => (value === null || value === undefined ? "—" : `${value}${suffix}`);

export function ForecastSourcePreference({ location, isDarkMode }) {
  const { user, updatePreferences } = useAuth();
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingSource, setSavingSource] = useState(null);

  const selectedSource = user?.preferences?.forecast_source_preference || "";

  const fetchComparison = useCallback(async () => {
    if (location?.lat === undefined || location?.lon === undefined) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/weather/enhanced/source-comparison`, {
        params: { lat: location.lat, lon: location.lon },
        timeout: 15000,
      });
      setComparison(response.data);
    } catch {
      setComparison(null);
    } finally {
      setLoading(false);
    }
  }, [location?.lat, location?.lon]);

  useEffect(() => {
    fetchComparison();
  }, [fetchComparison]);

  const selectedDetails = useMemo(() => {
    const sourceId = selectedSource || comparison?.recommended_source || "blend";
    return comparison?.sources?.find((source) => source.id === sourceId);
  }, [comparison, selectedSource]);

  const savePreference = async (sourceId) => {
    setSavingSource(sourceId);
    try {
      await axios.put(`${API}/user/preferences`, { forecast_source_preference: sourceId });
      updatePreferences({ forecast_source_preference: sourceId });
      toast.success("Forecast source preference saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save forecast source");
    } finally {
      setSavingSource(null);
    }
  };

  if (!location) return null;

  const cardClass = isDarkMode
    ? "border-white/10 bg-black/35 text-white"
    : "border-slate-200 bg-white/90 text-slate-950";
  const mutedText = isDarkMode ? "text-white/55" : "text-slate-600";
  const sourceCardClass = isDarkMode
    ? "border-white/10 bg-white/[0.04]"
    : "border-slate-200 bg-slate-50";

  return (
    <section className={`mb-4 rounded-2xl border p-4 shadow-sm backdrop-blur-xl ${cardClass}`} data-testid="forecast-source-preference-card">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="mb-1 flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-[#00f2ea]" />
            <h2 className="text-base font-black" data-testid="forecast-source-preference-title">
              {selectedSource ? "Your Forecast Source" : "Choose Your Forecast Source"}
            </h2>
          </div>
          <p className={`max-w-3xl text-sm ${mutedText}`} data-testid="forecast-source-preference-description">
            Compare available forecast sources for {location.name}. Pick the one that usually matches your place best; you can change this anytime.
          </p>
        </div>
        {selectedDetails && (
          <div className={`shrink-0 rounded-xl border px-3 py-2 text-sm ${sourceAccent[selectedDetails.id] || sourceAccent.blend}`} data-testid="forecast-source-current-choice">
            <span className="font-black">Using:</span> {selectedDetails.name}
          </div>
        )}
      </div>

      {loading && !comparison ? (
        <div className="mt-4 flex items-center gap-2 text-sm" data-testid="forecast-source-loading">
          <Loader2 className="h-4 w-4 animate-spin text-[#00f2ea]" />
          Checking forecast sources
        </div>
      ) : comparison?.sources?.length ? (
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="forecast-source-options-grid">
          {comparison.sources.map((source) => {
            const active = (selectedSource || comparison.recommended_source) === source.id;
            return (
              <article key={source.id} className={`rounded-xl border p-3 ${sourceCardClass} ${active ? "ring-2 ring-[#00f2ea]/60" : ""}`} data-testid={`forecast-source-option-${source.id}`}>
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate text-sm font-black" data-testid={`forecast-source-name-${source.id}`}>{source.name}</h3>
                    <p className={`mt-1 line-clamp-2 text-xs ${mutedText}`} data-testid={`forecast-source-summary-${source.id}`}>{source.summary}</p>
                  </div>
                  {active ? <CheckCircle2 className="h-4 w-4 shrink-0 text-[#00f2ea]" /> : <CloudSun className="h-4 w-4 shrink-0 opacity-50" />}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs" data-testid={`forecast-source-metrics-${source.id}`}>
                  <span>Temp <b>{formatValue(source.temperature, "°")}</b></span>
                  <span>Rain <b>{formatValue(source.rain_chance, "%")}</b></span>
                  <span>High <b>{formatValue(source.high, "°")}</b></span>
                  <span>Wind <b>{formatValue(source.wind_mph, " mph")}</b></span>
                </div>
                <p className={`mt-3 min-h-[34px] text-xs ${mutedText}`} data-testid={`forecast-source-details-${source.id}`}>{source.details}</p>
                <Button
                  type="button"
                  size="sm"
                  disabled={savingSource === source.id}
                  onClick={() => savePreference(source.id)}
                  className={`mt-3 h-9 w-full rounded-lg ${active ? "bg-[#00f2ea] text-black hover:bg-[#00f2ea]/90" : "bg-white/10 hover:bg-white/15"}`}
                  data-testid={`forecast-source-select-${source.id}-button`}
                >
                  {savingSource === source.id ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
                  {active ? "Selected" : "Use this"}
                </Button>
              </article>
            );
          })}
        </div>
      ) : (
        <div className={`mt-4 rounded-xl border p-3 text-sm ${sourceCardClass}`} data-testid="forecast-source-unavailable">
          Source comparison is unavailable right now.
        </div>
      )}
    </section>
  );
}